import { useState } from 'react'
import { post } from '../lib/api'
import { useTalents } from '../hooks/useTalents'
import { gettext } from '../lib/i18n'
import type { Talent, CreateTalentBody } from '../types/api'
import {
  getAvatarGradient,
  PIPELINE_STAGES,
  getStageIndex,
  getTierColor,
  predictSuccessRate,
  formatPercent,
} from '../lib/talent-utils'
import { Modal, useToast } from '../components/ui'
import styles from './DashboardPage.module.css'

function getInitial(name: string, initial: string | null) {
  if (initial) return initial
  return name.charAt(0).toUpperCase()
}

function tierClass(tier: number) {
  if (tier === 1) return styles.tier1
  if (tier === 2) return styles.tier2
  return styles.tier3
}

function matchScoreColor(score: number): string {
  if (score >= 80) return 'var(--green)'
  if (score >= 60) return 'var(--cyan)'
  if (score >= 40) return 'var(--amber)'
  return 'var(--red)'
}

function stageLabel(idx: number): string {
  const labels = [
    gettext('Source'),
    gettext('Resume'),
    gettext('Matching'),
    gettext('Cover Letter'),
    gettext('Done'),
  ]
  return labels[idx] ?? ''
}

function tierLabel(tier: number): string {
  const labels = [gettext('Challenge'), gettext('Stable'), gettext('Stable+')]
  return labels[tier - 1] ?? ''
}

function TalentCard({
  talent,
  featured = false,
}: {
  talent: Talent
  featured?: boolean
}) {
  const stageIdx = getStageIndex(talent.pipelineStage)
  const successRate = predictSuccessRate(talent.matchScore, talent.pipelineStage)

  return (
    <div
      className={`${styles.card} ${featured ? styles.cardFeatured : ''}`}
      onClick={() => {
        window.location.href = `/talents/${talent.id}/sources`
      }}
    >
      <div className={styles.cardTop}>
        <div
          className={styles.avatar}
          style={{ background: getAvatarGradient(talent.name) }}
        >
          {getInitial(talent.name, talent.avatarInitial)}
        </div>
        <div className={styles.cardInfo}>
          <div className={styles.cardName}>{talent.name}</div>
          {talent.targetRole ? (
            <div className={styles.cardRole}>{talent.targetRole}</div>
          ) : (
            <div className={styles.cardRoleMuted}>
              {gettext('Target role not set — set in Sources')}
            </div>
          )}
        </div>
        {talent.matchScore != null && (
          <span
            className={styles.matchBadge}
            style={{
              background: `color-mix(in srgb, ${matchScoreColor(talent.matchScore)} 15%, transparent)`,
              color: matchScoreColor(talent.matchScore),
            }}
          >
            <span className={styles.matchBadgeValue}>{talent.matchScore}%</span>
            <span className={styles.matchBadgeLabel}>
              {gettext('Match')}
            </span>
          </span>
        )}
      </div>

      <div className={styles.predictRow}>
        <span className={styles.predictLabel}>
          {gettext('Predicted Success Rate')}
        </span>
        <span className={styles.predictValue}>{formatPercent(successRate)}</span>
        <div className={styles.predictBar}>
          <div
            className={styles.predictFill}
            style={{
              width: `${successRate}%`,
              background: getTierColor(talent.tier),
            }}
          />
        </div>
        <span className={styles.predictHint}>{gettext('estimated')}</span>
      </div>

      <div className={styles.pipeline}>
        {PIPELINE_STAGES.map((s, i) => (
          <div
            key={s}
            className={`${styles.step} ${i <= stageIdx ? styles.stepActive : ''}`}
          />
        ))}
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.stepLabel}>
          {stageLabel(getStageIndex(talent.pipelineStage))}
        </span>
        <span className={`${styles.tierBadge} ${tierClass(talent.tier)}`}>
          {tierLabel(talent.tier)} · Tier {talent.tier}
        </span>
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const { talents, loading, error, reload } = useTalents()
  const [adding, setAdding] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [talentName, setTalentName] = useState('')
  const toast = useToast()

  const totalCount = talents.length
  const avgScore =
    talents.length > 0
      ? Math.round(
          talents.reduce((sum, t) => sum + (t.matchScore ?? 0), 0) /
            talents.length,
        )
      : 0
  const avgSuccess =
    talents.length > 0
      ? Math.round(
          talents.reduce(
            (sum, t) =>
              sum + predictSuccessRate(t.matchScore, t.pipelineStage),
            0,
          ) / talents.length,
        )
      : 0

  const tierGroups = [1, 2, 3]
    .map((tier) => ({
      tier,
      items: talents.filter((t) => t.tier === tier),
    }))
    .filter((g) => g.items.length > 0)

  const handleAdd = () => {
    setTalentName('')
    setAddModalOpen(true)
  }

  const handleSubmitTalent = async () => {
    const name = talentName.trim()
    if (!name) return

    setAdding(true)
    try {
      await post<Talent>('/api/v1/talents', {
        name,
      } satisfies CreateTalentBody)
      await reload()
      setAddModalOpen(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : gettext('Add failed'))
    } finally {
      setAdding(false)
    }
  }

  const addTalentModal = (
    <Modal
      open={addModalOpen}
      onClose={() => setAddModalOpen(false)}
      title={gettext('Enter talent name')}
    >
      <form
        className={styles.addForm}
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmitTalent()
        }}
      >
        <input
          className={styles.addInput}
          type="text"
          value={talentName}
          onChange={(e) => setTalentName(e.target.value)}
          placeholder={gettext('Enter talent name')}
          autoFocus
        />
        <div className={styles.addFormActions}>
          <button
            type="button"
            className={styles.addCancelBtn}
            onClick={() => setAddModalOpen(false)}
            disabled={adding}
          >
            {gettext('Cancel')}
          </button>
          <button
            type="submit"
            className={styles.addSubmitBtn}
            disabled={adding || !talentName.trim()}
          >
            {adding ? gettext('Adding...') : gettext('Add')}
          </button>
        </div>
      </form>
    </Modal>
  )

  if (loading) {
    return (
      <>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>{gettext('Dashboard')}</h1>
        </div>
        <div className={styles.loadingWrap}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles.skeleton} />
          ))}
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>{gettext('Dashboard')}</h1>
        </div>
        <div className={styles.errorWrap}>
          <span className={styles.errorMsg}>{error}</span>
          <button className={styles.retryBtn} onClick={reload}>
            {gettext('Retry')}
          </button>
        </div>
      </>
    )
  }

  if (talents.length === 0) {
    return (
      <>
        <div className={styles.headerRow}>
          <h1 className={styles.title}>{gettext('Dashboard')}</h1>
        </div>
        <div className={styles.emptyWrap}>
          <div className={styles.emptyIcon}>👤</div>
          <div>{gettext('Add your first talent')}</div>
          <button className={styles.ctaBtn} onClick={handleAdd} disabled={adding}>
            {`+ ${gettext('Add Talent')}`}
          </button>
        </div>
        {addTalentModal}
      </>
    )
  }

  const single = talents.length === 1

  return (
    <>
      <div className={styles.headerRow}>
        <h1 className={styles.title}>{gettext('Dashboard')}</h1>
        <button
          className={styles.ctaBtn}
          onClick={handleAdd}
          disabled={adding}
        >
          {`+ ${gettext('Add Talent')}`}
        </button>
      </div>

      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>👤</div>
          <div className={styles.statLabel}>{gettext('Talent count')}</div>
          <div className={styles.statValue}>{totalCount}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>🎯</div>
          <div className={styles.statLabel}>{gettext('Average Match')}</div>
          <div className={styles.statValue}>{avgScore}%</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>✨</div>
          <div className={styles.statLabel}>
            {gettext('Average Success Rate')}
          </div>
          <div className={styles.statValue}>{avgSuccess}%</div>
        </div>
      </div>

      {single ? (
        <div className={styles.featuredWrap}>
          <TalentCard talent={talents[0]} featured />
        </div>
      ) : (
        <div className={styles.tierList}>
          {tierGroups.map((g) => (
            <section key={g.tier} className={styles.tierGroup}>
              <div className={styles.tierHeader}>
                <span
                  className={styles.tierHeaderDot}
                  style={{ background: getTierColor(g.tier) }}
                />
                <span
                  className={styles.tierHeaderLabel}
                  style={{ color: getTierColor(g.tier) }}
                >
                  {tierLabel(g.tier)}
                </span>
                <span className={styles.tierHeaderCount}>
                  {gettext('{count} people · Tier {tier}', {
                    count: g.items.length,
                    tier: g.tier,
                  })}
                </span>
              </div>
              <div className={styles.grid}>
                {g.items.map((t) => (
                  <TalentCard key={t.id} talent={t} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      {addTalentModal}
    </>
  )
}
